import React, { useEffect, useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  useTheme,
  IconButton,
} from "@mui/material";
import { Add, Close, Delete, ShoppingCartCheckout } from "@mui/icons-material";
import { PreorderFormProps } from "./PreorderForm.types";
import { PreorderCartItem } from "./molecules/PreorderCartItem";
import { CommentSection } from "./molecules/CommentSection";
import { RecommendedItem } from "./molecules/RecommendedItem";
import { useCart } from "@/hooks/useCart";
import { useOrder } from "@/hooks/useOrder";
import { useAppSelector } from "@/core/store";
import { selectBrandId, selectPointId } from "@/core/app/selectors";
import { OrderType } from "@/types/generated/graphql-types";
import { numprice } from "@/helpers";
import { useProducts } from "../DishList/hooks/useProducts";

const PreorderForm: React.FC<PreorderFormProps> = ({
  onEditItem,
  onAddMore,
  onItemSelect,
  initialComment = "",
  externalLoading = false,
  onClose,
}) => {
  const theme = useTheme();

  // Use cart hook for all cart operations
  const {
    cartItems,
    totalSum,
    comment,
    loading: cartLoading,
    error: cartError,
    updateQuantity,
    updateComment,
    clearCart,
    isEmpty,
    isValid,
  } = useCart();

  // Use order hook for order creation
  const {
    createOrderFromCart,
    loading: orderLoading,
    error: orderError,
  } = useOrder();

  // Get brandId and pointId from app state
  const brandId = useAppSelector(selectBrandId);
  const pointId = useAppSelector(selectPointId);

  // Order creation state
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderCreationError, setOrderCreationError] = useState<string | null>(
    null
  );

  // Determine overall loading state
  const loading = cartLoading || externalLoading || orderLoading;

  const { products: recommendedItems } = useProducts({ brandId, pointId });

  // Initialize comment if provided
  useEffect(() => {
    if (initialComment && !comment) {
      updateComment(initialComment);
    }
  }, [initialComment, comment, updateComment]);

  const handleCommentChange = useCallback(
    (value: string) => {
      updateComment(value);
    },
    [updateComment]
  );

  const handleQuantityChange = useCallback(
    (itemId: string, quantity: number) => {
      // updateQuantity(itemId, quantity);
    },
    [updateQuantity]
  );

  const handleClear = useCallback(() => {
    clearCart();
    onClose?.();
  }, [clearCart, onClose]);

  const handleEditItem = useCallback(
    (itemId: string) => {
      onEditItem(itemId);
    },
    [onEditItem]
  );

  const handleCreateOrder = useCallback(async () => {
    // Clear previous errors and success state
    setOrderCreationError(null);
    setOrderSuccess(false);

    // Validate required parameters
    if (!brandId || !pointId) {
      setOrderCreationError(
        "Не указаны обязательные параметры ресторана. Обновите страницу."
      );
      return;
    }

    // Validate cart has items
    if (isEmpty || !isValid) {
      setOrderCreationError(
        "Корзина пуста или недействительна. Добавьте позиции в заказ."
      );
      return;
    }

    try {
      await createOrderFromCart({
        brandId,
        pointId,
        type: OrderType.PreOrder,
        comment: comment || undefined,
      });

      // Show success message
      setOrderSuccess(true);

      // Clear cart after successful order creation
      setTimeout(() => {
        clearCart();
        setOrderSuccess(false);
      }, 3000);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Не удалось создать заказ";
      setOrderCreationError(errorMessage);
    }
  }, [
    brandId,
    pointId,
    isEmpty,
    isValid,
    comment,
    createOrderFromCart,
    clearCart,
  ]);

  // Check if order creation is available
  const canCreateOrder = brandId && pointId && !isEmpty && isValid && !loading;

  const memoizedCartItems = useMemo(() => {
    return cartItems.map((item) => (
      <PreorderCartItem
        key={item.uuid}
        item={item}
        onQuantityChange={(quantity) =>
          handleQuantityChange(item.uuid, quantity)
        }
        onEditItem={() => handleEditItem(item.uuid)}
        disabled={loading}
      />
    ));
  }, [cartItems, handleQuantityChange, handleEditItem, loading]);

  return (
    <Box flex={1} display="flex" flexDirection="column" maxWidth="100%">
      {/* Header */}
      <Box
        sx={{
          pt: 1,
          pl: 3,
          pr: 1,
          pb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: 26,
            lineHeight: 1.1,
            pt: 1,
          }}
        >
          Ваш предзаказ
        </Typography>

        <IconButton onClick={onClose} sx={{ backgroundColor: "action.focus" }}>
          <Close color="primary" />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
        }}
      >
        <Box px={3}>
          {/* Cart Items Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Cart Items List */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingTop: 4,
                gap: 1.5,
              }}
            >
              {memoizedCartItems}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 3.75,
                alignItems: "center",
                marginTop: 1,
              }}
            >
              {/* Add More Button */}
              <Button
                onClick={onAddMore}
                disabled={loading}
                startIcon={<Add sx={{ fontSize: 16 }} />}
                variant="outlined"
                sx={{
                  flex: 1,
                  height: 40,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: "transparent",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                  boxShadow: `0px 1px 2px 0px ${theme.palette.action.hover}`,
                  "&:hover": {
                    borderColor: theme.palette.action.selected,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                Добавить ещё
              </Button>

              {/* Clear Button */}
              <Button
                onClick={handleClear}
                disabled={loading}
                startIcon={<Delete />}
                sx={{
                  textTransform: "none",
                  color: theme.palette.text.secondary,
                  minWidth: "auto",
                }}
              >
                Очистить
              </Button>
            </Box>

            {/* Comment Section */}
            <CommentSection
              value={comment}
              onChange={handleCommentChange}
              disabled={loading}
            />

            {/* Order Creation Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Success Message */}
              {orderSuccess && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Заказ успешно создан! Корзина будет очищена через несколько
                  секунд.
                </Alert>
              )}

              {/* Error Messages */}
              {(orderCreationError || cartError || orderError) && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {orderCreationError || cartError || orderError}
                </Alert>
              )}

              {/* Order Creation Help Text */}
              {isEmpty && (
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: "center",
                    color: "secondary",
                    fontSize: "12px",
                  }}
                >
                  Добавьте позиции в корзину для создания заказа
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Recommended Items Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            py: 2,
            pl: 3,
          }}
        >
          {/* Section Header */}
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              mb: 1.5,
              lineHeight: 1.1,
            }}
          >
            Добавьте ещё
          </Typography>

          {/* Recommended Items Grid */}
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                paddingBottom: 1,
                "&::-webkit-scrollbar": {
                  height: 4,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: 2,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.action.selected,
                  borderRadius: 2,
                },
              }}
            >
              {recommendedItems.map((item) => (
                <RecommendedItem
                  key={item.id}
                  item={item}
                  onSelect={() => onItemSelect(item.id)}
                  disabled={loading}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          p: 2,
          backgroundColor: "background.paper",
          boxShadow: `0px 0px 34px 0px rgba(0, 0, 0, 0.25);`,
          borderRadius: "24px 24px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography fontSize={14}>{cartItems.length} позиции</Typography>
          <Typography fontSize={20} fontWeight={600}>
            {numprice(totalSum)} ₽
          </Typography>
        </Box>
        <Button
          onClick={handleCreateOrder}
          disabled={!canCreateOrder}
          startIcon={<ShoppingCartCheckout sx={{ fontSize: 18 }} />}
          variant="contained"
          sx={{
            height: 50,
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 16,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: `0px 2px 4px 0px ${theme.palette.primary.main}30`,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: `0px 4px 8px 0px ${theme.palette.primary.main}40`,
            },
            "&:disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
            },
          }}
        >
          {loading ? "Создание заказа..." : "К брони и оплате"}
        </Button>
      </Box>
    </Box>
  );
};

export default PreorderForm;
